import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { axiosInstance } from "../../utils/axiosInstance";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
};

export default function HelpdeskEmailTicketDialog({ open, onClose, onCreated }: Props) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerDisplayName, setCustomerDisplayName] = useState("");
  const [articleSubject, setArticleSubject] = useState("");
  const [sendCustomerEmail, setSendCustomerEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setTitle("");
    setBody("");
    setCustomerEmail("");
    setCustomerDisplayName("");
    setArticleSubject("");
    setSendCustomerEmail(false);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    setError(null);
    setLoading(true);
    try {
      await axiosInstance.post("/customer-service/helpdesk/tickets/zammad/email", {
        title: title.trim(),
        body: body.trim(),
        customerEmail: customerEmail.trim(),
        customerDisplayName: customerDisplayName.trim() || null,
        articleSubject: articleSubject.trim() || null,
        sendCustomerEmail,
      });
      reset();
      onCreated();
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        "Failed to create ticket.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        New Zammad ticket (email)
        <IconButton aria-label="close" onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box display="flex" flexDirection="column" gap={2} pt={0.5}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
            size="small"
          />
          <TextField
            label="Customer email"
            type="email"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
            required
            fullWidth
            size="small"
          />
          <TextField
            label="Customer name (optional)"
            value={customerDisplayName}
            onChange={(e) => setCustomerDisplayName(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Article subject (optional)"
            value={articleSubject}
            onChange={(e) => setArticleSubject(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Message body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
            fullWidth
            multiline
            minRows={5}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={sendCustomerEmail}
                onChange={(e) => setSendCustomerEmail(e.target.checked)}
                color="primary"
              />
            }
            label="Send email to customer (Zammad outbound)"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void submit()} disabled={loading}>
          Create ticket
        </Button>
      </DialogActions>
    </Dialog>
  );
}
